import React, { forwardRef, useContext, useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { SimpleDropdown } from "../dropdown/SimpleDropdown";
import CloseIcon from "@components/icons/CloseIcon";
import { TxItemText } from "./TxItemText";
import { CoinBadge } from "@components/CoinBadge";
import { WasmTx } from "wasmx";
import { useCoins } from "@contexts/CoinsContext";
import { Coin, Coins, Fee, TxInfo } from "@terra-money/terra.js";
import { useSelectedChain } from "@app/examples/ChainSelector";
import { ConnectType, useConnectedWallet, useWallet } from "@terra-money/wallet-provider";
import { TxModalContext } from "@contexts/TxModalContext";
import { useLCD } from "@hooks/LCDHook";
import useAlert from "@hooks/AlertHook";
import { useAccount } from "@contexts/AccountContext";

export function TxModal() {
    const { wasmTx, callback, hide } = useContext(TxModalContext);
    const { getCoin } = useCoins()
    const { selectedChain: chain } = useSelectedChain()
    const { wallet } = useAccount()
    const { refetchStates, connection } = useWallet()
    const alert = useAlert()
    const lcd = useLCD()
    const [fee, setFee] = useState<Coin>()

    useEffect(() => {
        if (wasmTx) {
            const coins = wasmTx?.getTotalFee();
            if (coins?.denoms().length > 0) {
                setFee(coins.get(coins.denoms()[0]))
            }
        }
    }, [wasmTx])

    const getTotal = () => {
        if (!wasmTx) return

        const totalAmount = wasmTx.getTotalAmount()
        return totalAmount.add(fee ?? new Coins()).add(wasmTx.getTotalTax())
    }

    const post = async () => {

        try {
            if (!wasmTx || !chain || !lcd) throw new Error("Cannot get information from blockchain, try again later");
            if (!wallet) throw new Error("Cannot get your wallet information, try to refresh the page");
            if (!fee) throw new Error("Please select fee denom");

            if (connection?.type == ConnectType.EXTENSION) refetchStates()
            alert.loading("Sign Transaction, if your wallet show nothing try to refresh the page")
            const txOptions = await wasmTx.getTxOptions(fee.denom);
            const result = await wallet.post(txOptions)
            const txHash = result.result.txhash
            alert.loading("Waiting response from blockchain")
            let remaining = 10;

            const interval = setInterval(() => {
                if (remaining === 0) {
                    alert.hide()
                    callback?.onError?.call(null, new Error("Transaction failed"))
                }

                lcd.tx.txInfo(txHash).then((result) => {
                    clearInterval(interval)
                    setTimeout(() => {
                        alert.hide()
                        callback?.onSuccess?.call(null, result)
                        hide()
                    }, 2000)
                }).catch(() => { })
                remaining--;
            }, 1000)

        } catch (e) {
            console.error(e)
            if (e instanceof Error) hide(e)
            else hide(new Error("" + e))
        }
    }

    return <>
        <Modal show={wasmTx !== undefined} backdrop="static" onHide={() => hide(new Error("Transaction closed by user"))} dialogClassName="max-w-xl" scrollable={true}>
            <Modal.Header>
                <Modal.Title>{wasmTx?.tx.memo ?? "Unknown transaction"}</Modal.Title>
                <button onClick={() => hide(new Error("Transaction closed by user"))} type="button" className="btn-close" aria-label="Close">
                    <CloseIcon />
                </button>
            </Modal.Header>
            <Modal.Body>
                <div className="p-6 text-center">

                    <div className="mb-2 flex items-center justify-between">
                        <span className="font-display text-sm font-semibold text-jacarta-700 dark:text-white">Item</span>
                        <span className="font-display text-sm font-semibold text-jacarta-700 dark:text-white">Subtotal</span>
                    </div>

                    {wasmTx && wasmTx.messages.map(({ label, msg }, index) => (
                        <div key={index} className="flex border-t border-b jacarta-100 py-4 dark:border-jacarta-600">

                            <TxItemText text={label} />

                            <div className="ml-auto">
                                {'coins' in msg && msg.coins.denoms().length > 0 ? msg.coins.map(coin => {
                                    const coinDetail = getCoin(coin.denom)
                                    if (!coinDetail) return

                                    return <CoinBadge key={coin.denom} size="sm" name={coinDetail.name} icon={coinDetail.icon} amount={coin.amount.toNumber()} />
                                }) : <span className="text-white">-</span>}

                            </div>
                        </div>
                    ))}

                    <div className="flex border-b border-jacarta-100 py-4 dark:border-jacarta-600">

                        <TxItemText text="Tax" size="lg" />

                        <div className="ml-auto">
                            {chain && wasmTx?.getTotalTax().map(coin => {
                                const coinDetail = getCoin(coin.denom)
                                if (!coinDetail) return

                                return <CoinBadge key={coin.denom} size="md" name={coinDetail.name} icon={coinDetail.icon} amount={coin.amount.toNumber()} />
                            })}
                            {wasmTx?.getTotalTax().denoms().length == 0 && <span className="text-white">-</span>}
                        </div>
                    </div>

                    <div className="flex items-center border-b border-jacarta-100 py-4 dark:border-jacarta-600">

                        <TxItemText text="Fee" size="lg" />

                        <div className="ml-auto mr-2">
                            {wasmTx?.tx?.fee &&
                                <SimpleDropdown
                                    onSelected={denom => setFee(wasmTx?.tx.fee!.amount.get(denom)!)}
                                    values={wasmTx.tx.fee.amount.toArray().reduce<{ [key: string]: string }>((prev, curr) => {
                                        const coinDetail = getCoin(curr.denom)
                                        if (coinDetail) prev[curr.denom] = coinDetail.name
                                        return prev
                                    }, {})}
                                    selectedIndex={0}
                                />
                            }
                        </div>
                        {fee && <CoinBadge key={"fee"} size="sm" name={getCoin(fee.denom)!.name} icon={getCoin(fee.denom)!.icon} amount={fee.amount.toNumber()} color="jacarta-500" />}
                    </div>

                    <div className="flex border-jacarta-100 py-4 mt-2 dark:border-jacarta-600">

                        <TxItemText text="Total" size="lg" />

                        <div className="ml-auto">
                            {getTotal()?.map(coin => {
                                const coinDetail = getCoin(coin.denom)
                                if (!coinDetail) return

                                return <CoinBadge key={"total"} size="md" name={coinDetail.name} icon={coinDetail.icon} useFormat={false} amount={coin.amount.toNumber()} />
                            })}
                        </div>
                    </div>
                </div>

            </Modal.Body>
            <Modal.Footer>
                <div className="flex items-center justify-center space-x-4">
                    <button onClick={post} type="button"
                        className="rounded-full bg-jacarta-500 py-3 px-8 text-center font-semibold text-white transition-all hover:bg-jacarta-600">
                        Confirm
                    </button>
                </div>
            </Modal.Footer>
        </Modal>
    </>
}